import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AppShell,
  DataTable,
  PageContainer,
  PageHeader,
  SectionContainer,
  StatusBadge,
  UIButton,
  mapCustomerStatusToTone,
  mapMonitoringStatusToTone,
} from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import { useOperationalDataset } from './hooks/useOperationalDataset';
import { createTicket, getAuthHeadersOrRedirect } from './utils/ticketingApi';
import { deriveCustomerImpactSummary } from './utils/customerImpact';
import { createExecutionLog, fetchExecutionLogs, getExecutionActionLabel } from './utils/executionLogApi';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import CustomerWorkflowDrawer from './components/CustomerWorkflowDrawer';

const API = `${resolveBackendBaseUrl()}/api`;

const hasValidCoordinates = (lat, lon) => {
  const latitude = Number(lat);
  const longitude = Number(lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const toOptionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildCustomerPayload = (formValues, isEditMode = false) => {
  const payload = {
    name: String(formValues?.customerName || '').trim(),
    address: String(formValues?.address || '').trim(),
    phone: String(formValues?.phone || '').trim(),
    email: String(formValues?.email || '').trim() || undefined,
    package: String(formValues?.packageId || '').trim(),
    pppoe_username: String(formValues?.pppoeUsername || '').trim(),
    pppoe_password: String(formValues?.pppoePassword || '').trim(),
    portal_password: String(formValues?.portalPassword || '').trim() || undefined,
    notes: String(formValues?.notes || '').trim() || undefined,
    accessType: String(formValues?.accessType || 'FTTH_PPPOE').trim().toUpperCase(),
    site_id: String(formValues?.siteId || '').trim() || undefined,
    router_id: String(formValues?.nasId || '').trim() || undefined,
    nas_id: String(formValues?.nasId || '').trim() || undefined,
    olt_id: String(formValues?.oltId || '').trim() || undefined,
    odp_id: String(formValues?.odpId || '').trim() || undefined,
    pon_port: String(formValues?.ponPort || '').trim() || undefined,
    onu_serial: String(formValues?.onuSerial || '').trim() || undefined,
    genieacs_device_id: String(formValues?.genieacsDeviceId || '').trim() || undefined,
    latitude: toOptionalNumber(formValues?.latitude),
    longitude: toOptionalNumber(formValues?.longitude),
    billing_cycle_day: toOptionalNumber(formValues?.billingCycleDay),
    first_due_date: String(formValues?.firstDueDate || '').trim() || undefined,
  };
  if (isEditMode && !payload.portal_password) {
    delete payload.portal_password;
  }
  return payload;
};

const toNetworkPath = (customer, odpsById, oltsById, nasById) => {
  const odpId = String(customer?.linked_odp_id || customer?.odp_id || '');
  const odp = odpsById[odpId];
  const olt = oltsById[String(odp?.olt_id || customer?.linked_olt_id || customer?.olt_id || '')];
  const nas = nasById[String(customer?.router_id || olt?.router_id || olt?.uplink_router_id || '')];
  return {
    odp,
    olt,
    nas,
    label: `${odp?.name || odpId || 'ODP -'} -> ${olt?.name || olt?.code || 'OLT -'} -> ${nas?.name || 'NAS -'}`,
  };
};

const derivePppStatus = (customer) => {
  const status = String(customer?.status_detail?.connection_status || customer?.status || '').toLowerCase();
  if (['online', 'active'].includes(status)) return 'online';
  if (['offline', 'down', 'terminated'].includes(status)) return 'offline';
  if (['degraded', 'warn', 'warning', 'suspended'].includes(status)) return 'degraded';
  return 'unknown';
};

const deriveOnuStatus = (customer) => {
  const lastInform = customer?.status_detail?.last_inform;
  if (!lastInform) return 'unknown';
  const parsed = new Date(lastInform);
  if (Number.isNaN(parsed.getTime())) return 'unknown';
  const diffMinutes = Math.floor((Date.now() - parsed.getTime()) / 60000);
  if (diffMinutes <= 15) return 'online';
  if (diffMinutes <= 180) return 'degraded';
  return 'offline';
};

const ticketStatusTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'closed') return 'ok';
  if (token === 'mitigated') return 'info';
  if (token === 'investigating') return 'warn';
  return 'neutral';
};

const ticketPriorityTone = (priority) => {
  const token = String(priority || '').trim().toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const executionActionTone = (actionType) => {
  const token = String(actionType || '').trim().toLowerCase();
  if (token === 'mitigation_completed') return 'ok';
  if (token === 'mitigation_started' || token === 'investigation_started' || token === 'backup_verified') return 'warn';
  if (token === 'ticket_created' || token === 'ticket_updated') return 'info';
  return 'neutral';
};

export default function AdminCustomers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, refresh, customers, odps, olts, nasRouters, sites, incidents, tickets, lastUpdated } = useOperationalDataset(navigate);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState('');
  const [ticketDraft, setTicketDraft] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
  });
  const [ticketSaving, setTicketSaving] = useState(false);
  const [showAllRelatedTickets, setShowAllRelatedTickets] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionError, setExecutionError] = useState('');
  const [executionNote, setExecutionNote] = useState('');
  const [executionSaving, setExecutionSaving] = useState(false);
  const [showAllExecutionLogs, setShowAllExecutionLogs] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [crudSaving, setCrudSaving] = useState(false);
  const [packageOptions, setPackageOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  const odpsById = useMemo(() => {
    const byId = {};
    odps.forEach((item) => { byId[String(item?.id || '')] = item; });
    return byId;
  }, [odps]);
  const oltsById = useMemo(() => {
    const byId = {};
    olts.forEach((item) => { byId[String(item?.id || '')] = item; });
    return byId;
  }, [olts]);
  const nasById = useMemo(() => {
    const byId = {};
    nasRouters.forEach((item) => { byId[String(item?.id || item?.router_id || '')] = item; });
    return byId;
  }, [nasRouters]);

  const filteredCustomers = useMemo(() => {
    const keyword = String(query || '').trim().toLowerCase();
    return customers.filter((customer) => {
      const status = String(customer?.status || '').toLowerCase();
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!keyword) return true;
      const hay = `${customer?.id || ''} ${customer?.customer_id || ''} ${customer?.name || ''} ${customer?.phone || ''}`.toLowerCase();
      return hay.includes(keyword);
    });
  }, [customers, query, statusFilter]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const customerId = String(params.get('customerId') || '').trim();
    const sourceType = String(params.get('sourceType') || '').trim().toLowerCase();
    const sourceId = String(params.get('sourceId') || '').trim();
    if (customerId) {
      setSelectedId(customerId);
      return;
    }
    if (sourceType === 'customer' && sourceId) {
      setSelectedId(sourceId);
      return;
    }
    const incidentId = String(params.get('incidentId') || '').trim();
    if (incidentId) {
      const linkedIncident = incidents.find((item) => String(item.id) === incidentId);
      const customerRef = linkedIncident?.impactedEntities?.find((item) => String(item).startsWith('customer:'));
      if (customerRef) setSelectedId(String(customerRef).split(':')[1]);
    }
  }, [location.search, incidents]);

  const selectedCustomer = useMemo(
    () => filteredCustomers.find((item) => String(item.id) === String(selectedId)) || filteredCustomers[0] || null,
    [filteredCustomers, selectedId],
  );

  const selectedPath = useMemo(
    () => (selectedCustomer ? toNetworkPath(selectedCustomer, odpsById, oltsById, nasById) : null),
    [selectedCustomer, odpsById, oltsById, nasById],
  );

  React.useEffect(() => {
    let cancelled = false;
    const loadCrudReferenceData = async () => {
      const headers = getAuthHeadersOrRedirect(navigate);
      if (!headers) return;
      try {
        const [packagesResponse, sitesResponse] = await Promise.all([
          axios.get(`${API}/packages`, { headers }),
          axios.get(`${API}/sites`, { headers }),
        ]);
        if (cancelled) return;
        setPackageOptions(Array.isArray(packagesResponse?.data) ? packagesResponse.data : []);
        setSiteOptions(Array.isArray(sitesResponse?.data) ? sitesResponse.data : []);
      } catch (_error) {
        if (cancelled) return;
        setPackageOptions([]);
        setSiteOptions([]);
      }
    };
    loadCrudReferenceData();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const relatedIncidents = useMemo(() => {
    if (!selectedCustomer) return [];
    const keys = [
      `customer:${selectedCustomer.id}`,
      selectedCustomer?.linked_odp_id ? `odp:${selectedCustomer.linked_odp_id}` : null,
      selectedCustomer?.linked_olt_id ? `olt:${selectedCustomer.linked_olt_id}` : null,
      selectedCustomer?.router_id ? `nas:${selectedCustomer.router_id}` : null,
    ].filter(Boolean);
    return incidents.filter((incident) => incident.impactedEntities.some((entity) => keys.includes(entity)));
  }, [selectedCustomer, incidents]);

  const relatedTickets = useMemo(() => {
    if (!selectedCustomer) return [];
    return tickets.filter((ticket) => (
      String(ticket.customerId || ticket.customer_id || '') === String(selectedCustomer.id || '')
      || (String(ticket.sourceType || ticket.source_type || '').toLowerCase() === 'customer'
        && String(ticket.sourceId || ticket.source_id || '') === String(selectedCustomer.id || ''))
    ));
  }, [selectedCustomer, tickets]);

  const customerImpactSummary = useMemo(() => deriveCustomerImpactSummary({
    context: selectedCustomer
      ? {
        type: 'customer',
        id: String(selectedCustomer.id || ''),
        customerId: String(selectedCustomer.id || ''),
        routerId: String(selectedCustomer.router_id || ''),
        siteId: String(selectedCustomer.site_id || ''),
      }
      : {},
    data: {
      customers,
      odps,
      olts,
      nasRouters,
      sites,
      incidents,
    },
  }), [selectedCustomer, customers, odps, olts, nasRouters, sites, incidents]);

  const activeRelatedTickets = useMemo(
    () => relatedTickets.filter((ticket) => String(ticket.status || '').toLowerCase() !== 'closed'),
    [relatedTickets],
  );

  const latestRelatedTicket = useMemo(
    () => relatedTickets[0] || null,
    [relatedTickets],
  );
  const mostRelevantRelatedTicket = useMemo(() => {
    if (!activeRelatedTickets.length) return latestRelatedTicket;
    const priorityRank = { critical: 3, high: 2, medium: 1, low: 0 };
    const ranked = activeRelatedTickets.slice().sort((a, b) => {
      const statusA = String(a.status || '').toLowerCase();
      const statusB = String(b.status || '').toLowerCase();
      const investigatingA = statusA === 'investigating' ? 1 : 0;
      const investigatingB = statusB === 'investigating' ? 1 : 0;
      if (investigatingB !== investigatingA) return investigatingB - investigatingA;
      const priorityA = priorityRank[String(a.priority || '').toLowerCase()] ?? 0;
      const priorityB = priorityRank[String(b.priority || '').toLowerCase()] ?? 0;
      if (priorityB !== priorityA) return priorityB - priorityA;
      const updatedA = new Date(a.updated_at || a.created_at || 0).getTime();
      const updatedB = new Date(b.updated_at || b.created_at || 0).getTime();
      return updatedB - updatedA;
    });
    return ranked[0] || latestRelatedTicket;
  }, [activeRelatedTickets, latestRelatedTicket]);

  const executionLogContext = useMemo(() => {
    if (!selectedCustomer) return null;
    return {
      sourceType: 'customer',
      sourceId: String(selectedCustomer.id || ''),
      customerId: String(selectedCustomer.id || ''),
      routerId: String(selectedPath?.nas?.id || selectedCustomer.router_id || ''),
      nodeId: String(selectedPath?.nas?.id || selectedCustomer.router_id || ''),
      siteId: String(selectedPath?.odp?.site_id || selectedCustomer.site_id || ''),
      incidentId: String(relatedIncidents[0]?.id || ''),
      ticketId: String(relatedTickets[0]?.id || ''),
    };
  }, [selectedCustomer, selectedPath, relatedIncidents, relatedTickets]);

  React.useEffect(() => {
    let cancelled = false;
    const loadExecutionLogs = async () => {
      if (!executionLogContext) {
        setExecutionLogs([]);
        setExecutionError('');
        setExecutionLoading(false);
        setShowAllExecutionLogs(false);
        return;
      }
      setExecutionLoading(true);
      setExecutionError('');
      try {
        const payload = await fetchExecutionLogs({
          apiBase: API,
          navigate,
          params: {
            source_type: executionLogContext.sourceType,
            source_id: executionLogContext.sourceId,
            customer_id: executionLogContext.customerId || undefined,
            incident_id: executionLogContext.incidentId || undefined,
            ticket_id: executionLogContext.ticketId || undefined,
            router_id: executionLogContext.routerId || undefined,
            site_id: executionLogContext.siteId || undefined,
            limit: 120,
          },
        });
        if (cancelled) return;
        setExecutionLogs(payload.items || []);
      } catch (error) {
        if (cancelled) return;
        setExecutionLogs([]);
        setExecutionError(String(error?.message || 'Gagal memuat execution log.'));
      } finally {
        if (!cancelled) setExecutionLoading(false);
      }
    };
    loadExecutionLogs();
    return () => {
      cancelled = true;
    };
  }, [executionLogContext, navigate]);

  const latestExecutionLog = useMemo(
    () => (Array.isArray(executionLogs) && executionLogs.length > 0 ? executionLogs[0] : null),
    [executionLogs],
  );

  const visibleExecutionLogs = useMemo(() => {
    const logs = Array.isArray(executionLogs) ? executionLogs : [];
    return showAllExecutionLogs ? logs : logs.slice(0, 3);
  }, [executionLogs, showAllExecutionLogs]);

  const openCreateCustomer = () => {
    setDrawerMode('create');
    setIsDrawerOpen(true);
  };

  const openEditCustomer = () => {
    if (!selectedCustomer) {
      toast.error('Pilih customer yang ingin diedit terlebih dahulu.');
      return;
    }
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  };

  const handleSubmitCustomerCrud = async (formValues) => {
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;
    const isEditMode = drawerMode === 'edit' && selectedCustomer;
    const payload = buildCustomerPayload(formValues, Boolean(isEditMode));
    if (!payload.name || !payload.phone || !payload.address || !payload.package || !payload.pppoe_username || !payload.pppoe_password) {
      toast.error('Data customer utama belum lengkap.');
      return;
    }
    setCrudSaving(true);
    try {
      if (isEditMode) {
        await axios.put(`${API}/customers/${encodeURIComponent(String(selectedCustomer.id || ''))}`, payload, { headers });
        toast.success(`Customer ${selectedCustomer.customer_id || selectedCustomer.id} berhasil diperbarui.`);
      } else {
        const createPayload = {
          ...payload,
          portal_password: payload.portal_password || payload.pppoe_password,
        };
        await axios.post(`${API}/customers`, createPayload, { headers });
        toast.success('Customer baru berhasil dibuat.');
      }
      setIsDrawerOpen(false);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Gagal menyimpan customer.');
    } finally {
      setCrudSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) {
      toast.error('Pilih customer yang ingin dihapus terlebih dahulu.');
      return;
    }
    const customerLabel = selectedCustomer.customer_id || selectedCustomer.id;
    const confirmed = window.confirm(`Hapus customer ${customerLabel}? Tindakan ini tidak bisa dibatalkan.`);
    if (!confirmed) return;
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;
    setCrudSaving(true);
    try {
      await axios.delete(`${API}/customers/${encodeURIComponent(String(selectedCustomer.id || ''))}`, { headers });
      toast.success(`Customer ${customerLabel} berhasil dihapus.`);
      setSelectedId('');
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Gagal menghapus customer.');
    } finally {
      setCrudSaving(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!selectedCustomer) return;
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;
    const title = String(ticketDraft.title || '').trim();
    if (!title) {
      toast.error('Judul ticket wajib diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const created = await createTicket({
        headers,
        payload: {
          title,
          description: ticketDraft.description,
          priority: ticketDraft.priority,
          assigned_to: ticketDraft.assigned_to || undefined,
          source_type: 'customer',
          source_id: String(selectedCustomer.id || ''),
          customer_id: String(selectedCustomer.id || ''),
      site_id: String(selectedCustomer.site_id || '').trim() || undefined,
        },
      });
      created.warnings.forEach((warning) => toast.warning(warning));
      toast.success(`Ticket ${created.item.ticketCode || created.item.id} dibuat.`);
      setTicketDraft({ title: '', description: '', priority: 'medium', assigned_to: '' });
      await refresh();
    } catch (err) {
      toast.error(err?.message || 'Gagal membuat ticket.');
    } finally {
      setTicketSaving(false);
    }
  };

  const createExecutionEntry = async ({ actionType, message }) => {
    if (!executionLogContext || !message) return null;
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: executionLogContext.sourceType,
          source_id: executionLogContext.sourceId,
          customer_id: executionLogContext.customerId || undefined,
          incident_id: executionLogContext.incidentId || undefined,
          ticket_id: executionLogContext.ticketId || undefined,
          router_id: executionLogContext.routerId || undefined,
          node_id: executionLogContext.nodeId || undefined,
          site_id: executionLogContext.siteId || undefined,
          action_type: actionType,
          message,
        },
      });
      if (created) {
        setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      return created;
    } catch (_error) {
      return null;
    }
  };

  const handleAddExecutionNote = async () => {
    const note = String(executionNote || '').trim();
    if (!note) {
      toast.error('Catatan execution log belum diisi.');
      return;
    }
    setExecutionSaving(true);
    try {
      const created = await createExecutionEntry({ actionType: 'manual_note', message: note });
      if (!created) {
        throw new Error('Execution log gagal disimpan.');
      }
      setExecutionNote('');
      toast.success('Execution log tersimpan.');
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menyimpan execution log.'));
    } finally {
      setExecutionSaving(false);
    }
  };

  const columns = [
    { key: 'client', label: 'Client' },
    { key: 'service', label: 'Service' },
    { key: 'monitoring', label: 'Monitoring' },
    { key: 'site', label: 'Site' },
    { key: 'path', label: 'Infra Path' },
  ];

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/customers" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Customers"
          title="Customer Management"
          subtitle="Kelola data pelanggan (create, edit, delete) sebagai flow utama, dengan impact operasional sebagai konteks tambahan."
          action={(
            <div className="flex flex-wrap items-center gap-2">
              <UIButton variant="subtle" onClick={openCreateCustomer} disabled={crudSaving}>
                <Plus className="h-4 w-4" />
                Tambah Customer
              </UIButton>
              <UIButton variant="ghost" onClick={openEditCustomer} disabled={crudSaving || !selectedCustomer}>
                <Pencil className="h-4 w-4" />
                Edit
              </UIButton>
              <UIButton variant="ghost" onClick={handleDeleteCustomer} disabled={crudSaving || !selectedCustomer}>
                <Trash2 className="h-4 w-4" />
                Delete
              </UIButton>
              <UIButton variant="ghost" onClick={refresh} disabled={loading || crudSaving}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </UIButton>
            </div>
          )}
        />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cari customer id / nama / telepon..."
            className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 md:max-w-md"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200"
          >
            <option value="all">Semua Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="degraded">Degraded</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <span className="ml-auto text-xs text-slate-500">
            Last update: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}
          </span>
        </div>

        {error ? (
          <SectionContainer className="mb-4 border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
            {error}
          </SectionContainer>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[1.18fr_0.82fr]">
          <SectionContainer>
            <DataTable
              title="Customer List"
              subtitle="Klik baris untuk lihat detail, lalu gunakan aksi CRUD di header untuk kelola data customer."
              columns={columns}
              rows={filteredCustomers}
              loading={loading}
              rowKey="id"
              onRowClick={(row) => setSelectedId(row.id)}
              emptyMessage="Tidak ada pelanggan pada scope ini. Sesuaikan filter atau kata kunci."
              renderCell={(row, column) => {
                if (column.key === 'client') {
                  return (
                    <div>
                      <p className="text-sm font-semibold text-white">{row.name || '-'}</p>
                      <p className="text-xs text-slate-400">{row.customer_id || row.id}</p>
                    </div>
                  );
                }
                if (column.key === 'service') {
                  return (
                    <div className="space-y-1">
                      <StatusBadge tone={mapCustomerStatusToTone(row.status || 'unknown')}>
                        {String(row.status || 'unknown').toUpperCase()}
                      </StatusBadge>
                      <p className="text-xs text-slate-400">{row.package_name || row.package || '-'}</p>
                    </div>
                  );
                }
                if (column.key === 'monitoring') {
                  return (
                    <StatusBadge tone={mapMonitoringStatusToTone(row.status || 'unknown')}>
                      {String(row.status || 'unknown').toUpperCase()}
                    </StatusBadge>
                  );
                }
                if (column.key === 'site') return row.site || row.site_name || '-';
                if (column.key === 'path') {
                  const path = toNetworkPath(row, odpsById, oltsById, nasById);
                  return <p className="text-xs text-slate-300">{path.label}</p>;
                }
                return '-';
              }}
            />
          </SectionContainer>

          <SectionContainer className="space-y-3">
            {!selectedCustomer ? (
              <p className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-4 text-sm text-slate-400">
                Tidak ada customer terpilih. Pilih baris dari tabel untuk melihat konteks detail.
              </p>
            ) : (
              <>
                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{selectedCustomer.name || '-'}</p>
                      <p className="text-xs text-slate-400">{selectedCustomer.customer_id || selectedCustomer.id}</p>
                    </div>
                    <StatusBadge tone={mapCustomerStatusToTone(selectedCustomer.status || 'unknown')}>
                      {String(selectedCustomer.status || 'unknown').toUpperCase()}
                    </StatusBadge>
                  </div>
                  <div className="grid gap-1 text-xs text-slate-300">
                    <p>Phone: <span className="text-white">{selectedCustomer.phone || '-'}</span></p>
                    <p>Address: <span className="text-white">{selectedCustomer.address || '-'}</span></p>
                    <p>Service status: <span className="text-white">{String(selectedCustomer.status || 'unknown').toUpperCase()}</span></p>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Execution Log</p>
                    <span className="text-[10px] text-slate-500">Total {executionLogs.length}</span>
                  </div>
                  {executionLoading ? <p className="text-xs text-slate-400">Masih memuat data...</p> : null}
                  {!executionLoading && executionError ? <p className="text-xs text-rose-300">{executionError}</p> : null}
                  {!executionLoading && !executionError && executionLogs.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada data terkait.</p>
                  ) : null}
                  {!executionLoading && !executionError && executionLogs.length > 0 ? (
                    <div className="space-y-1.5">
                      {visibleExecutionLogs.map((entry) => (
                        <div key={entry.id || `${entry.createdAt}-${entry.message}`} className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold text-slate-100">{getExecutionActionLabel(entry.actionType)}</p>
                            <StatusBadge tone={executionActionTone(entry.actionType)}>{entry.actor || '-'}</StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{entry.message}</p>
                          <p className="mt-1 text-[10px] text-slate-500">{entry.createdAt ? new Date(entry.createdAt).toLocaleString('id-ID') : '-'}</p>
                        </div>
                      ))}
                      {executionLogs.length > 3 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllExecutionLogs((prev) => !prev)}
                        >
                          {showAllExecutionLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 flex gap-2">
                    <input
                      value={executionNote}
                      onChange={(event) => setExecutionNote(event.target.value)}
                      placeholder="Tambah catatan runbook operasional"
                      className="flex-1 rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <UIButton variant="ghost" onClick={handleAddExecutionNote} disabled={executionSaving}>Simpan Note</UIButton>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Last Operational Update</p>
                  {latestExecutionLog ? (
                    <div className="space-y-1 text-xs text-slate-300">
                      <p className="font-semibold text-white">{getExecutionActionLabel(latestExecutionLog.actionType)}</p>
                      <p>{latestExecutionLog.message || '-'}</p>
                      <p className="text-[11px] text-slate-500">
                        {latestExecutionLog.actor || '-'} | {latestExecutionLog.createdAt ? new Date(latestExecutionLog.createdAt).toLocaleString('id-ID') : '-'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Belum ada tindakan tercatat.</p>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">ONU / PPP Telemetry</p>
                  <div className="grid gap-1 text-xs text-slate-300">
                    <p>
                      ONU:
                      {' '}
                      <StatusBadge tone={mapMonitoringStatusToTone(deriveOnuStatus(selectedCustomer))}>
                        {deriveOnuStatus(selectedCustomer).toUpperCase()}
                      </StatusBadge>
                    </p>
                    <p>
                      PPP:
                      {' '}
                      <StatusBadge tone={mapMonitoringStatusToTone(derivePppStatus(selectedCustomer))}>
                        {derivePppStatus(selectedCustomer).toUpperCase()}
                      </StatusBadge>
                    </p>
                    <p>ONU Serial: <span className="text-white">{selectedCustomer.onu_serial || selectedCustomer.ont_serial || '-'}</span></p>
                    <p>PPPoE User: <span className="text-white">{selectedCustomer.pppoe_username || '-'}</span></p>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Operational Impact</p>
                    <StatusBadge tone={customerImpactSummary.severityTone}>{String(customerImpactSummary.severityLabel || 'low').toUpperCase()}</StatusBadge>
                  </div>
                  {customerImpactSummary.state !== 'ok' ? (
                    <p className="text-xs text-slate-400">
                      {customerImpactSummary.state === 'empty'
                        ? 'Belum ada dampak operasional aktif untuk customer ini.'
                        : customerImpactSummary.message || 'Context dampak belum lengkap.'}
                    </p>
                  ) : (
                    <div className="space-y-1.5 text-xs text-slate-300">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge tone="neutral">Impacted {customerImpactSummary.counts.total}</StatusBadge>
                        <StatusBadge tone={customerImpactSummary.counts.offline > 0 ? 'danger' : 'neutral'}>Offline {customerImpactSummary.counts.offline}</StatusBadge>
                        <StatusBadge tone={customerImpactSummary.counts.degraded > 0 ? 'warn' : 'neutral'}>Degraded {customerImpactSummary.counts.degraded}</StatusBadge>
                      </div>
                      <p>Incident aktif terkait: <span className="text-white">{relatedIncidents.length}</span></p>
                      <p>Incident terbaru: <span className="text-white">{relatedIncidents[0] ? `${relatedIncidents[0].id} - ${relatedIncidents[0].title}` : 'Belum ada incident terkait'}</span></p>
                      <p>Ticket aktif terkait: <span className="text-white">{activeRelatedTickets.length}</span></p>
                      <p>Ticket paling relevan: <span className="text-white">{mostRelevantRelatedTicket ? `${mostRelevantRelatedTicket.ticketCode || mostRelevantRelatedTicket.id} (${String(mostRelevantRelatedTicket.status || 'open').toLowerCase()})` : 'Belum ada ticket aktif terkait'}</span></p>
                      <p>
                        Router/node terkait:{' '}
                        <span className="text-white">{selectedPath?.nas?.name || selectedCustomer.router_id || '-'}</span>
                      </p>
                      <p>
                        Site/path terkait:{' '}
                        <span className="text-white">{selectedPath?.odp?.site_name || selectedCustomer.site_name || selectedCustomer.site_id || '-'}</span>
                      </p>
                      <p>Status penanganan terakhir: <span className="text-white">{latestExecutionLog ? `${getExecutionActionLabel(latestExecutionLog.actionType)} | ${latestExecutionLog.createdAt ? new Date(latestExecutionLog.createdAt).toLocaleString('id-ID') : '-'}` : 'Belum ada update operasional'}</span></p>
                      {customerImpactSummary.mitigation.hint ? (
                        <p className="text-cyan-200">{customerImpactSummary.mitigation.hint}</p>
                      ) : (
                        <p className="text-slate-500">Belum ada hint mitigasi path untuk customer ini.</p>
                      )}
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <UIButton
                      as="link"
                      to={relatedIncidents[0]
                        ? `/recovery?incidentId=${encodeURIComponent(relatedIncidents[0].id)}&customerId=${encodeURIComponent(String(selectedCustomer.id || ''))}`
                        : `/recovery?customerId=${encodeURIComponent(String(selectedCustomer.id || ''))}`}
                      variant="ghost"
                      onClick={() => {
                        const message = relatedIncidents[0]
                          ? `Recovery dibuka untuk customer ${selectedCustomer.customer_id || selectedCustomer.id} (incident ${relatedIncidents[0].id}).`
                          : `Recovery dibuka untuk customer ${selectedCustomer.customer_id || selectedCustomer.id}.`;
                        void createExecutionEntry({ actionType: 'recovery_opened', message });
                      }}
                    >
                      Open in Recovery
                    </UIButton>
                    <UIButton
                      as="link"
                      to={hasValidCoordinates(selectedCustomer.latitude, selectedCustomer.longitude)
                        ? `/topology?mode=live&sourceType=customer&sourceId=${encodeURIComponent(String(selectedCustomer.id || ''))}`
                        : '/topology?mode=live'}
                      variant="ghost"
                      onClick={() => {
                        void createExecutionEntry({
                          actionType: 'topology_opened',
                          message: `Topology dibuka untuk customer ${selectedCustomer.customer_id || selectedCustomer.id}.`,
                        });
                      }}
                    >
                      Open in Topology
                    </UIButton>
                    <UIButton
                      as="link"
                      to={mostRelevantRelatedTicket
                        ? `/tickets?ticketId=${encodeURIComponent(String(mostRelevantRelatedTicket.id || ''))}`
                        : '/tickets'}
                      variant="ghost"
                      className="col-span-2"
                    >
                      Open in Tickets
                    </UIButton>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">ODP / OLT / Router-NAS Path</p>
                  <p className="text-xs text-slate-200">{selectedPath?.label || 'Relasi belum lengkap'}</p>
                  {!selectedPath?.odp || !selectedPath?.olt || !selectedPath?.nas ? (
                    <p className="mt-1 text-xs text-slate-500">Relasi belum lengkap. Mapping ODP/OLT/NAS perlu dilengkapi.</p>
                  ) : null}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Related Incident / Ticket</p>
                  <p className="mb-2 text-[11px] text-slate-400">
                    Context customer: <span className="text-slate-200">{selectedCustomer.customer_id || selectedCustomer.id}</span>
                  </p>
                  {relatedIncidents.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada incident terkait customer ini, atau relasi incident belum lengkap.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {relatedIncidents.slice(0, 4).map((incident) => (
                        <div key={incident.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{incident.title}</p>
                            <StatusBadge tone={severityToTone(incident.severity)}>{incident.severity.toUpperCase()}</StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-400">{incident.summary}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {relatedTickets.length ? (
                    <div className="mt-2 space-y-1.5">
                      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-slate-400">
                        <span>Aktif: <span className="text-slate-100">{activeRelatedTickets.length}</span></span>
                        <span>Terbaru: <span className="text-slate-100">{latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}</span></span>
                      </div>
                      {(showAllRelatedTickets ? relatedTickets : relatedTickets.slice(0, 3)).map((ticket) => (
                        <div key={ticket.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{ticket.ticketCode || ticket.id}</p>
                            <StatusBadge tone={ticketStatusTone(ticket.status)}>{String(ticket.status || 'open').toLowerCase()}</StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{ticket.title || '-'}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <StatusBadge tone={ticketPriorityTone(ticket.priority)}>{String(ticket.priority || 'medium').toLowerCase()}</StatusBadge>
                            <span className="text-[10px] text-slate-500">PIC: {ticket.assignedTo || '-'}</span>
                            <span className="text-[10px] text-slate-500">Update: {ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('id-ID') : '-'}</span>
                          </div>
                        </div>
                      ))}
                      {relatedTickets.length > 3 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllRelatedTickets((prev) => !prev)}
                        >
                          {showAllRelatedTickets ? 'Tampilkan ringkas' : 'Tampilkan lebih banyak'}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500">Belum ada ticket terkait. Buat ticket untuk mulai melacak penanganan.</p>
                  )}
                  <div className="mt-2 space-y-2 rounded-md border border-white/10 bg-[#101b2e] p-2.5">
                    <p className="text-[11px] uppercase tracking-[0.1em] text-slate-400">Buat Ticket</p>
                    <input
                      value={ticketDraft.title}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder={`Gangguan customer ${selectedCustomer.customer_id || selectedCustomer.id}`}
                      className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <textarea
                      value={ticketDraft.description}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, description: event.target.value }))}
                      rows={2}
                      placeholder="Catatan awal penanganan"
                      className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={ticketDraft.priority}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, priority: event.target.value }))}
                        className="rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-200"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="critical">critical</option>
                      </select>
                      <input
                        value={ticketDraft.assigned_to}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, assigned_to: event.target.value }))}
                        placeholder="PIC / owner"
                        className="rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                    <UIButton variant="subtle" onClick={handleCreateTicket} disabled={ticketSaving}>
                      Buat Ticket
                    </UIButton>
                    {activeRelatedTickets.length > 0 ? (
                      <p className="text-[10px] text-amber-300">
                        Sudah ada {activeRelatedTickets.length} ticket aktif untuk customer ini. Ticket aktif terbaru: {latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}.
                        Anda tetap bisa membuat ticket baru jika memang perlu.
                      </p>
                    ) : null}
                  </div>
                </div>

                {!hasValidCoordinates(selectedCustomer.latitude, selectedCustomer.longitude) ? (
                  <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Map Context</p>
                    <p className="mt-1 text-xs text-slate-500">Koordinat map belum tersedia untuk customer ini. Topology tetap bisa dibuka tanpa auto-focus marker.</p>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-2">
                  <UIButton
                    as="link"
                    to={hasValidCoordinates(selectedCustomer.latitude, selectedCustomer.longitude)
                      ? `/topology?mode=live&sourceType=customer&sourceId=${encodeURIComponent(String(selectedCustomer.id || ''))}`
                      : '/topology?mode=live'}
                    variant="subtle"
                    onClick={() => {
                      void createExecutionEntry({
                        actionType: 'topology_opened',
                        message: `Topology dibuka untuk customer ${selectedCustomer.customer_id || selectedCustomer.id}.`,
                      });
                    }}
                    >
                    <ArrowUpRight className="h-4 w-4" />
                    Open in Topology
                  </UIButton>
                  <UIButton
                    as="link"
                    to={relatedIncidents[0]
                      ? `/recovery?incidentId=${encodeURIComponent(relatedIncidents[0].id)}&customerId=${encodeURIComponent(String(selectedCustomer.id || ''))}`
                      : `/recovery?customerId=${encodeURIComponent(String(selectedCustomer.id || ''))}`}
                    variant="ghost"
                    onClick={() => {
                      const message = relatedIncidents[0]
                        ? `Recovery dibuka untuk customer ${selectedCustomer.customer_id || selectedCustomer.id} (incident ${relatedIncidents[0].id}).`
                        : `Recovery dibuka untuk customer ${selectedCustomer.customer_id || selectedCustomer.id}.`;
                      void createExecutionEntry({ actionType: 'recovery_opened', message });
                    }}
                  >
                    Open in Recovery
                  </UIButton>
                  <UIButton
                    as="link"
                    to={selectedPath?.odp?.id
                      ? `/infrastructure?nodeType=odp&nodeId=${encodeURIComponent(String(selectedPath.odp.id || ''))}`
                      : '/infrastructure'}
                    variant="ghost"
                    className="col-span-2"
                  >
                    Open in Infrastructure
                  </UIButton>
                </div>
              </>
            )}
          </SectionContainer>
        </div>
      </PageContainer>
      <CustomerWorkflowDrawer
        open={isDrawerOpen}
        onClose={() => {
          if (crudSaving) return;
          setIsDrawerOpen(false);
        }}
        onSubmit={handleSubmitCustomerCrud}
        customer={drawerMode === 'edit' ? selectedCustomer : null}
        packageOptions={packageOptions}
        sites={siteOptions}
        nasRouters={nasRouters}
        olts={olts}
        odps={odps}
      />
    </AppShell>
  );
}

const severityToTone = (severity) => {
  if (severity === 'critical') return 'danger';
  if (severity === 'high') return 'warn';
  return 'info';
};






